<!--
 * App Layout
 * --------------------
 * Main layout component for the shell application.
 * Responsible for rendering the global structure of the app including:
 *
 *  - Application header
 *  - Left navigation (product-specific)
 *  - Micro-frontend (MFE) content outlet
 *  - Global SPA loader (store-driven)
 *
 * Responsibilities:
 *  1. Waits until all micro frontends signal readiness via `shellReady`.
 *  2. Dynamically loads the correct sidebar menu based on the current route.
 *     - Shows the menu only for product routes:
 *         • /accounts  → Marketing menu
 *         • /service   → Service menu
 *         • /commerce  → Commerce menu
 *  3. Reactively updates the menu when the route changes
 *     without reloading the application.
 *  4. Displays a global Vuetify loader during SPA route transitions
 *     and micro-frontend loading (controlled via Pinia store).
 *
 * Implementation details:
 *  - Uses `createMenuForRoute()` from `unifiedMenu.ts`.
 *  - Uses Vuetify `v-overlay` + `v-progress-circular`.
 *  - Compatible with Vue 3 + single-spa shell architecture.
 *
 * @author Harvinder Singh <harvinder@maropost.com>
 * @created 2025-09-30
 * @updated 2026-02-10
 -->

 <template>
  <div>
    <!-- ===============================================================
         GLOBAL HEADER
         =============================================================== -->
    <TheHeader />

    <!-- ===============================================================
         TRIAL BANNER
         =============================================================== -->

    <div
      v-if="trialDays && trialDays.maxDays && showBanner"
      class="trial-banner"
    >
        <v-icon size="18">
          mdi-information-outline
        </v-icon>
        <div class="trial-text">   {{isTrialExpired ? 'Trial Ended' : 'Trial: ' + trialDays.maxDays + ' left until your trial ends.'}}</div>

      <a href="#" @click="navigateToCheckout" class="trial-link">BUY NOW</a>

      <v-icon size="16" class="trial-close-icon" @click="hideBanner">
          mdi-close
        </v-icon>
    </div>
    
    <!-- ===============================================================
         GLOBAL SPA LOADER
         =============================================================== -->
         <v-overlay
            :model-value="loading"
            class="spa-loader"
            persistent
            width="100%"
            height="100%"
            opacity="0.25"
          >
          <v-row justify="center" align="center" class="loader-row">
              <v-progress-circular
                indeterminate
                size="48"
                width="4"
                color="primary"
              />
            </v-row>
          </v-overlay>
    <!-- ===============================================================
         APPLICATION LAYOUT
         =============================================================== -->
    <v-main class="mt-16">
      <v-row no-gutters>
        <!-- -----------------------------------------------------------
             LEFT NAVIGATION
             Rendered only when product menu is available
        ------------------------------------------------------------ -->
        <v-col cols="auto" v-if="showNavigation">
          <TheUnifiedNavigation
            class="mt-16 content-area index"
            :account-id="userStore.getAccountId" 
            :nav-blocks="navigation" 
            :enableStoreMenu="saleNavigation"
            :collapsed="userStore.isNavigationRail"
            @update:collapsed="toggleLeftMenu"
          >
          <template #store-control>
            <div id="sale-channel-navigation"></div> 
          </template>
          <template #top-block>
            <OnboardingMenu />
          </template>
          </TheUnifiedNavigation>
        </v-col>

        <!-- -----------------------------------------------------------
             MAIN CONTENT AREA
        ------------------------------------------------------------ -->
        <v-col cols="12">
          <div class="content-area">
            <DrawerHost ref="drawerRef" />

            <router-view />
          </div>
        </v-col>
      </v-row>

      <AlertModal :show="alertModalState.show" :state="alertModalItems" @confirm="handleAlertConfirm"  @cancel="alertModalService.hide()" />

      <AppSnackbar
        v-model="showSnackbar"
        :type="snackbarMessage?.type"
        :title="snackbarMessage?.title"
        :message="snackbarMessage?.message"
        :timeout="snackbarMessage?.timeout"
        @close="shellBridgeStore.setSnackbarMessage({})"
      />
    </v-main>
  </div>
</template>

<script setup lang="ts">
/**
 * ============================================================================
 * Unified Shell – Navigation & Loader Controller
 * ============================================================================
 *
 * Behavior Summary
 * ----------------
 * - Displays unified navigation ONLY for supported product routes
 *   (Marketing, Service, Commerce).
 * - Navigation menu is loaded lazily and reacts to route changes.
 * - Global loader visibility is controlled via cross-MFE events.
 *
 * Communication Model
 * -------------------
 * - MFEs dispatch `MFE_LOADED` events to signal loading state.
 * - Shell listens and updates `loading` accordingly.
 * - MFEs can also request navigation rail changes via custom events.
 *
 * Design Notes
 * ------------
 * - Shell remains framework-agnostic.
 * - MFEs do not directly import shell dependencies.
 * - All UI side-effects are centralized here.
 * ============================================================================
 */

import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { navigateToUrl } from "single-spa";

/* ---------------------------------------------------------------------------
 * UI Components
 * ---------------------------------------------------------------------------
 */
import { TheHeader, DrawerHost } from "../components";
import {
  TheUnifiedNavigation,
  AlertModal,
  AppSnackbar,
  GenericModalState,
} from "@maropost-ui/liquidsky-ui";
import OnboardingMenu from "../components/onboarding/NavigationMenu.vue";

/* ---------------------------------------------------------------------------
 * Stores & Services
 * ---------------------------------------------------------------------------
 */
import { useUserStore, useShellBridgeStore } from "../stores";
import { useNavigation } from "../composable/sidemenu/unifiedNavigation";
import { alertModalService } from "@/lib/services/alertModalService";
import { onBoardingService } from "@/lib/services/onBoardingService";
import { shouldFetchOnboardingData } from "@/lib/utils/onboardingAccess";
import { useHubSpotChat } from "@/lib/plugins/hubspotChat";
import { getPurchaseChangeType } from "@/lib/utils/packages";
import { getPreviousProductPlans } from "@/lib/utils/previousProductPlans";
import {
  trackAmplitudeEvent,
  triggerSnackbar,
} from "@/lib/utils/utilityFunctions";

import { useGlobalTrialState } from "../composable/freeTrail/useGlobalTrialState";

/* ============================================================================
 * STORES & ROUTER
 * ============================================================================
 */

// User / permissions store
const userStore = useUserStore();

// Vue Router instance
const router = useRouter();
const route = useRoute();

// Global alert modal reactive state
const alertModalState = alertModalService.getState();

// Global loader state (controlled by MFEs)
const loading = ref(false);

// Show banner state
const showBanner = computed(() => userStore.getCTABanner);
const hubSpotChat = useHubSpotChat();
const shellBridgeStore = useShellBridgeStore();

const snackbarMessage = computed(
  () => shellBridgeStore.getSnackbarMessage
)

const showSnackbar = computed({
  get: () => Object.entries(snackbarMessage.value).length > 0 ,
  set: (value) => {
    if (!value) {
      shellBridgeStore.setSnackbarMessage({});
    }
  },
})
/**
 * Load HubSpot chat after the shell loader is idle (initial mount or MFE finished).
 */
const loadHubSpotWhenIdle = (): void => {
  if (!loading.value) {
    hubSpotChat.loadIfEnabled();
  }
};

onMounted(loadHubSpotWhenIdle);

watch(loading, loadHubSpotWhenIdle, { flush: "post" });

// Sales navigation visibility (permission-based)
const saleNavigation = computed(() => userStore.getSaleNavigation);

// Normalized alert modal props
const alertModalItems = computed<GenericModalState>(() => ({
  title: alertModalState.title,
  subtitle: alertModalState.subtitle,
  description: alertModalState.description,
}));

  /**
   * Get the trial data
   */
  const trialData:any = computed(() => userStore.getTrailPackageFeatures);

  /**
   * Use the trial countdown composable
   */

  const { trialDays } = useGlobalTrialState(
    trialData,
    userStore
  );

  /**
   * Hide the banner
   */
  const hideBanner = () => {
    userStore.setCTABanner(false);
  }

  /**
   * Navigate to the billing page
   */
  const navigateToCheckout = () => {
    let accountId = userStore.getAccountId
    navigateToUrl(`/accounts/${accountId}/checkout`)  }

  /**
   * Check if the trial is expired
   */
   const isTrialExpired = computed(() => userStore.getIsTrailAccount && userStore.getIsTrialExpired);


   /**
    * 
    */
   const isNetoMhcAccountTraialExpired = computed(() => userStore.getIsTrailAccount && userStore.getIsTrialExpired && !userStore.getProductAccess('neto') && !userStore.getProductAccess('mhc'));

/**
 * Left navigation visibility.
 * Hidden on routes that opt out via meta (e.g. account setup) or when
 * a Neto/MHC trial account has expired.
 */
const showNavigation = computed(() => {
  const navigationEnabled = (route.meta.navigation as { show?: boolean } | undefined)?.show !== false
  return navigationEnabled && !isNetoMhcAccountTraialExpired.value
});

/* ============================================================================
 * NAVIGATION STATE
 * ============================================================================
 */

/**
 * Unified navigation configuration
 * - Generated dynamically based on user permissions
 * - Passed directly to <TheUnifiedNavigation />
 */
const { navigation } = useNavigation();

/**
 * Shows a snackbar when a purchase is successful
 */
function showPurchaseSuccessSnackbar(): void {
  const previousProductPlans = getPreviousProductPlans(userStore.getAccountId);
  if (!previousProductPlans || userStore.isLegacyAccount) return;

  const hasCoreProducts =
    userStore.getProductAccess("marketing") ||
    userStore.getProductAccess("service") ||
    userStore.getProductAccess("commerce");

  if (!hasCoreProducts) return;

  let message = "";

  for (const productKey of ["mmc", "mcc", "msc"] as const) {
    const changeType = getPurchaseChangeType(
      previousProductPlans[productKey],
      userStore.getCurrentPackagePlan(productKey),
      productKey
    );

    if (changeType === "upgrade") {
      message =
        "Your payment was successful and your plan has been updated.";
      trackAmplitudeEvent("Checkout Completed", { isFirstPurchase: false });
      break;
    }

    if (changeType === "first_purchase") {
      message =
        "Your payment was successful and your subscription is now active.";
      trackAmplitudeEvent("Checkout Completed", { isFirstPurchase: true });
    }
  }

  if (message) {
    triggerSnackbar({ message, type: "success" });
  }
}

onMounted(() => {
  if (shouldFetchOnboardingData(userStore)) {
    onBoardingService.getOnboardingData().catch((err) => {
      console.error("Failed to fetch onboarding data:", err);
    });
  }

  showPurchaseSuccessSnackbar();
});

/* ============================================================================
 * CROSS-MFE EVENT HANDLERS
 * ============================================================================
 */

/**
 * Handle MFE loader lifecycle events
 *
 * Event: MFE_LOADED
 * Payload:
 *   - true  → show loader
 *   - false → hide loader
 *
 * Dispatched by:
 *   - Shell (before mounting/unmounting MFEs)
 *   - Individual MFEs (when fully initialized)
 */
const onMfeLoaded = (event: Event) => {
  const customEvent = event as CustomEvent<boolean>;
  loading.value = customEvent.detail;
};

// Register loader event listener
window.addEventListener("MFE_LOADED", onMfeLoaded);

/**
 * Handle navigation rail toggle requests from MFEs
 *
 * Event: mfe:folder-open
 * Payload:
 *   - true  → open navigation rail
 *   - false → close navigation rail
 *
 * Used when MFEs want to temporarily control layout
 * (e.g. opening a folder or deep view)
 */
window.addEventListener("mfe:folder-open", (event) => {
  const customEvent = event as CustomEvent<boolean>;
  if (customEvent.detail !== undefined) {
    userStore.setNavigationRail(customEvent.detail);
  }
});

/* ============================================================================
 * UI ACTIONS
 * ============================================================================
 */

/**
 * Toggle left navigation drawer manually
 * (Triggered by header / hamburger icon)
 */
const toggleLeftMenu = (isDrawerClosed: boolean): void => {
  userStore.setNavigationRail(isDrawerClosed);
};

/**
 * Handle alert modal confirmation
 *
 * Scenario:
 * - User hits usage limit for a feature
 * - Redirect them to Account & Billing page
 *
 * Routing Logic:
 * - Marketing users → SPA navigation
 * - Non-marketing users → full page redirect
 */
const handleAlertConfirm = () => {
  const accountId = userStore.getAccountId;
  const path = `/accounts/${accountId}/${userStore.isLegacyAccount ? 'account-&-billing/subscriptions' : 'checkout'}`;

  if (
    !userStore.getProductAccess("marketing") ||
    !userStore.getProductAccessOnRoles("marketing")
  ) {
    router.push(path);
  } else {
    navigateToUrl(path);
  }

  alertModalService.hide();
};
</script>


<style>
/* ============================================================================
   GLOBAL LOADER
============================================================================ */

.loader-row {
  height: 100%;
}

/* ============================================================================
   LAYOUT
============================================================================ */

.content-area {
  height: calc(100vh - 64px) !important;
  overflow: auto;
  background: #fafafa;
}
.index {
  z-index: 10 !important;
}
</style>

  
<style scoped>
.trial-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #FFF6E5;
  padding: 4px 12px;
  font-size: 14px;
  position: absolute;
  top: 68px;
  right: 15px;
  z-index: 2;
  box-shadow: 0px 1px #2F2B3D1F;
  border-radius: 4px;
}

.trial-icon {
  font-size: 14px;
}

.trial-text {
  color: #333;
  font-weight: 500;
  margin-top: 1px;
}

.trial-link {
  margin-left: auto;
  color: #008ECD;
  font-weight: 600;
  text-decoration: underline;
}

.trial-close-icon {
  cursor: pointer;
  margin-left: auto;
}
</style>